import React, { FC, useMemo } from 'react';
import { Card, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { groupBy } from 'lodash';

import { AmountField, Space } from '~/components';
import { Amount, BalanceType } from '~/types';
import {
  CurrentAvailableIcon,
  CurrentPendingIcon,
  CurrentPostedIcon,
  ProcessedInRealTimeIcon,
} from '~/img';
import constants from '~/utils/constants';
import { useAccountsQuery } from '~/hooks/accounts';
import { useDirection } from '~/hooks';
import GridLayout from '~/layout/GridLayout';
import theme from '~/theme';
import layout from '~/hooks/layout';
import features from '~/featuresWithDefaults';

const Amounts: FC = () => {
  const { data, isLoading } = useAccountsQuery();

  const isDesktop = layout.useIsDesktop();

  /**
   * example of object
   * {
   *   USD: {
   *     CURRENT_AVAILABLE: 1000,
   *     CURRENT_PENDING: 0,
   *     CURRENT_POSTED: 123,
   *   },
   *   ZAR: {
   *     CURRENT_AVAILABLE: 0,
   *     CURRENT_PENDING: 9,
   *     CURRENT_POSTED: 13,
   *   },
   *   ...
   * }
   * */
  const balancesTotals: Record<string, Record<BalanceType, number>> = useMemo(() => {
    const allBalances = data
      ?.filter((item) => item.status !== 'CLOSED')
      ?.map((item) => item.balances)
      .flat(1);

    return Object.fromEntries(
      Object.entries(
        // group all balances by currency
        groupBy(allBalances, (item) => item?.amount?.currency)
      ).map(
        // for each currency in balance group
        ([cur, balancesInSameCurrency]) => [
          cur,
          Object.fromEntries(
            Object.entries(
              // group balances by balance type
              groupBy(balancesInSameCurrency, (item) => item.balanceType)
            )
              // filter balance types by feature
              .filter(([balanceType]) => features.balanceTypesOnDashboard.includes(balanceType))
              // sum amount of balances of same type
              .map(([balanceType, list]) => [
                balanceType as any,
                list.reduce((prev, bal) => prev + (bal?.amount?.value ?? 0), 0),
              ])
          ),
        ]
      )
    );
  }, [data]);

  if (isLoading) return <Spin />;
  return (
    <Space direction="vertical">
      {Object.entries(balancesTotals).map(([currency, balances]) => {
        const columns = Object.keys(balances).length;
        return (
          <GridLayout gutter={[16, 16]} xs={24} lg={columns > 3 ? (isDesktop ? 6 : 12) : 8}>
            {Object.entries(balances).map(([balanceType, total], index) => {
              return (
                <BalanceCard
                  key={index.toString()}
                  amount={{ value: total, currency }}
                  balanceType={balanceType as BalanceType}
                />
              );
            })}
          </GridLayout>
        );
      })}
    </Space>
  );
};

interface BalanceCardProps {
  amount?: Amount;
  balanceType: BalanceType;
}

const BalanceCard: FC<BalanceCardProps> = (props) => {
  const { balanceType, amount } = props;
  const [t] = useTranslation();
  const getIcon = () => {
    switch (balanceType) {
      case 'CURRENT_POSTED':
        return <CurrentPostedIcon style={{ color: getBalanceColor(balanceType) }} />;
      case 'CURRENT_AVAILABLE':
        return <CurrentAvailableIcon style={{ color: getBalanceColor(balanceType) }} />;
      case 'CURRENT_PENDING':
        return <CurrentPendingIcon style={{ color: getBalanceColor(balanceType) }} />;
      case 'PROCESSED_IN_REALTIME':
        return <ProcessedInRealTimeIcon style={{ color: getBalanceColor(balanceType) }} />;
      case 'CURRENT_FLOAT':
        return <CurrentAvailableIcon style={{ color: getBalanceColor(balanceType) }} />;
      default:
        return null;
    }
  };

  const [direction] = useDirection();

  return (
    <StyledCard $balanceType={balanceType} direction={direction}>
      <div className="BalanceCard-Icon">{getIcon()}</div>
      <div>
        <AmountField amount={amount} className="BalanceCard-Amount" />
        {balanceType ? <div className="BalanceCard-BalanceType">{t(balanceType)}</div> : null}
      </div>
    </StyledCard>
  );
};

const getBalanceColor = (balanceType: string | undefined) => {
  switch (balanceType) {
    case 'CURRENT_POSTED':
      return constants.balanceColorPosted;
    case 'CURRENT_AVAILABLE':
      return constants.balanceColorAvailable;
    case 'CURRENT_PENDING':
      return constants.balanceColorPending;
    case 'PROCESSED_IN_REALTIME':
      return constants.processedInRealTime;
    case 'CURRENT_FLOAT':
      return constants.currentFloat;
    default:
      return 'unset';
  }
};

interface StyledCardProps {
  $balanceType: BalanceCardProps['balanceType'];
  direction: 'ltr' | 'rtl';
}

const StyledCard = styled<FC<StyledCardProps>>(Card as any)`
  .ant-card-body {
    display: flex;
    line-height: 1;
    .BalanceCard-Icon {
      font-size: 52px;
      display: flex;
      align-items: center;
      margin-left: ${({ direction }) => (direction === 'rtl' ? '24px' : '0px')};
      margin-right: ${({ direction }) => (direction === 'ltr' ? '24px' : '0px')};
      @media (max-width: ${theme.breakpoints.sm}px) {
        font-size: 36px;
        margin-left: ${({ direction }) => (direction === 'rtl' ? '16px' : '0px')};
        margin-right: ${({ direction }) => (direction === 'ltr' ? '16px' : '0px')};
      }
    }
    .BalanceCard-Amount {
      font-size: 28px;
      margin-bottom: 12px;
      color: ${(props) => getBalanceColor(props.$balanceType)};
      font-weight: 600;
      @media (max-width: ${theme.breakpoints.sm}px) {
        font-size: 18px;
        line-height: 24px;
        margin-bottom: 4px;
      }
    }
    .BalanceCard-BalanceType {
      @media (max-width: ${theme.breakpoints.sm}px) {
        font-size: 12px;
        line-height: 14px;
      }
    }
    @media (max-width: ${theme.breakpoints.sm}px) {
      padding: 10px 16px;
      align-items: center;
    }
  }
`;

export default Amounts;
